import { EstadoExpediente } from "@copi/shared";

/**
 * Categorías semánticas que mapean a tokens CSS de `globals.css` (§7.2).
 * Mantener este tipo sincronizado con `--state-*` variables.
 */
export type EstadoCategoria =
  | "pendiente"
  | "en-revision"
  | "observado"
  | "aprobado"
  | "insuficiente"
  | "desaprobado"
  | "vencido"
  | "finalizado";

/**
 * Mapeo declarativo de cada estado del workflow a una categoría semántica.
 * Editar aquí si cambia la política de color — el componente `EstadoBadge` se
 * mantiene sin tocar (OCP).
 */
const CATEGORIA_POR_ESTADO: Record<EstadoExpediente, EstadoCategoria> = {
  [EstadoExpediente.BORRADOR_SOLICITUD]: "pendiente",
  [EstadoExpediente.ASESORIA_SOLICITADA]: "en-revision",
  [EstadoExpediente.ASESORIA_ACEPTADA]: "aprobado",
  [EstadoExpediente.SOLICITUD_PRESENTADA]: "en-revision",
  [EstadoExpediente.SOLICITUD_EN_REVISION_DECANATO]: "en-revision",
  [EstadoExpediente.SOLICITUD_OBSERVADA]: "observado",
  [EstadoExpediente.SOLICITUD_RECHAZADA]: "desaprobado",
  [EstadoExpediente.ASESOR_DESIGNADO]: "aprobado",
  [EstadoExpediente.PROYECTO_EN_ELABORACION]: "en-revision",
  [EstadoExpediente.PROYECTO_EN_REVISION_ASESOR]: "en-revision",
  [EstadoExpediente.PROYECTO_CON_CONFORMIDAD_ASESOR]: "aprobado",
  [EstadoExpediente.JURADO_DESIGNADO]: "aprobado",
  [EstadoExpediente.PROYECTO_EN_REVISION_JURADO]: "en-revision",
  [EstadoExpediente.PROYECTO_OBSERVADO]: "observado",
  [EstadoExpediente.PROYECTO_RECHAZADO]: "desaprobado",
  [EstadoExpediente.PROYECTO_APROBADO]: "aprobado",
  [EstadoExpediente.TESIS_EN_EJECUCION]: "en-revision",
  [EstadoExpediente.PLAZO_EJECUCION_VENCIDO]: "vencido",
  [EstadoExpediente.BORRADOR_TESIS_PRESENTADO]: "en-revision",
  [EstadoExpediente.BORRADOR_EN_REVISION_JURADO]: "en-revision",
  [EstadoExpediente.BORRADOR_OBSERVADO]: "observado",
  [EstadoExpediente.BORRADOR_SUBSANADO]: "en-revision",
  [EstadoExpediente.BORRADOR_INSUFICIENTE]: "insuficiente",
  [EstadoExpediente.BORRADOR_SUFICIENTE]: "aprobado",
  [EstadoExpediente.EN_CONTROL_ANTIPLAGIO]: "en-revision",
  [EstadoExpediente.ANTIPLAGIO_OBSERVADO]: "observado",
  [EstadoExpediente.ANTIPLAGIO_APROBADO]: "aprobado",
  [EstadoExpediente.FECHA_SUSTENTACION_SOLICITADA]: "en-revision",
  [EstadoExpediente.SUSTENTACION_PROGRAMADA]: "aprobado",
  [EstadoExpediente.SUSTENTACION_REPROGRAMADA]: "observado",
  [EstadoExpediente.SUSTENTACION_REALIZADA]: "en-revision",
  [EstadoExpediente.SUSTENTACION_APROBADA]: "aprobado",
  [EstadoExpediente.SUSTENTACION_DESAPROBADA]: "desaprobado",
  [EstadoExpediente.TESIS_REGISTRADA_REPOSITORIO]: "aprobado",
  [EstadoExpediente.TITULO_EN_TRAMITE]: "en-revision",
  [EstadoExpediente.TITULO_OTORGADO]: "finalizado",
  [EstadoExpediente.EXPEDIENTE_OBSERVADO]: "observado",
  [EstadoExpediente.TRAMITE_SUSPENDIDO]: "vencido",
};

const LABEL_POR_ESTADO: Record<EstadoExpediente, string> = {
  [EstadoExpediente.BORRADOR_SOLICITUD]: "Borrador de solicitud",
  [EstadoExpediente.ASESORIA_SOLICITADA]: "Asesoría solicitada al asesor",
  [EstadoExpediente.ASESORIA_ACEPTADA]: "Asesoría aceptada por asesor",
  [EstadoExpediente.SOLICITUD_PRESENTADA]: "Solicitud presentada",
  [EstadoExpediente.SOLICITUD_EN_REVISION_DECANATO]: "En revisión por Decanato",
  [EstadoExpediente.SOLICITUD_OBSERVADA]: "Solicitud observada",
  [EstadoExpediente.SOLICITUD_RECHAZADA]: "Solicitud rechazada",
  [EstadoExpediente.ASESOR_DESIGNADO]: "Asesor designado",
  [EstadoExpediente.PROYECTO_EN_ELABORACION]: "Proyecto en elaboración",
  [EstadoExpediente.PROYECTO_EN_REVISION_ASESOR]: "Proyecto en revisión del asesor",
  [EstadoExpediente.PROYECTO_CON_CONFORMIDAD_ASESOR]: "Con conformidad del asesor",
  [EstadoExpediente.JURADO_DESIGNADO]: "Jurado designado",
  [EstadoExpediente.PROYECTO_EN_REVISION_JURADO]: "Proyecto en revisión por jurado",
  [EstadoExpediente.PROYECTO_OBSERVADO]: "Proyecto observado",
  [EstadoExpediente.PROYECTO_RECHAZADO]: "Proyecto rechazado",
  [EstadoExpediente.PROYECTO_APROBADO]: "Proyecto aprobado",
  [EstadoExpediente.TESIS_EN_EJECUCION]: "Tesis en ejecución",
  [EstadoExpediente.PLAZO_EJECUCION_VENCIDO]: "Plazo de ejecución vencido",
  [EstadoExpediente.BORRADOR_TESIS_PRESENTADO]: "Borrador presentado",
  [EstadoExpediente.BORRADOR_EN_REVISION_JURADO]: "Borrador en revisión por jurado",
  [EstadoExpediente.BORRADOR_OBSERVADO]: "Borrador observado",
  [EstadoExpediente.BORRADOR_SUBSANADO]: "Borrador subsanado",
  [EstadoExpediente.BORRADOR_INSUFICIENTE]: "Borrador insuficiente",
  [EstadoExpediente.BORRADOR_SUFICIENTE]: "Borrador suficiente",
  [EstadoExpediente.EN_CONTROL_ANTIPLAGIO]: "En control de antiplagio",
  [EstadoExpediente.ANTIPLAGIO_OBSERVADO]: "Antiplagio observado",
  [EstadoExpediente.ANTIPLAGIO_APROBADO]: "Antiplagio aprobado",
  [EstadoExpediente.FECHA_SUSTENTACION_SOLICITADA]: "Fecha de sustentación solicitada",
  [EstadoExpediente.SUSTENTACION_PROGRAMADA]: "Sustentación programada",
  [EstadoExpediente.SUSTENTACION_REPROGRAMADA]: "Sustentación reprogramada",
  [EstadoExpediente.SUSTENTACION_REALIZADA]: "Sustentación realizada",
  [EstadoExpediente.SUSTENTACION_APROBADA]: "Sustentación aprobada",
  [EstadoExpediente.SUSTENTACION_DESAPROBADA]: "Sustentación desaprobada",
  [EstadoExpediente.TESIS_REGISTRADA_REPOSITORIO]: "Tesis registrada en repositorio",
  [EstadoExpediente.TITULO_EN_TRAMITE]: "Título en trámite",
  [EstadoExpediente.TITULO_OTORGADO]: "Título otorgado",
  [EstadoExpediente.EXPEDIENTE_OBSERVADO]: "Expediente observado",
  [EstadoExpediente.TRAMITE_SUSPENDIDO]: "Trámite suspendido",
};

export function categoriaDeEstado(estado: EstadoExpediente): EstadoCategoria {
  return CATEGORIA_POR_ESTADO[estado];
}

export function labelDeEstado(estado: EstadoExpediente): string {
  return LABEL_POR_ESTADO[estado];
}

/**
 * Clases Tailwind para badge según categoría. Usa tokens `--state-*`
 * definidos en `globals.css` con opacidad para fondo.
 */
export const CLASES_POR_CATEGORIA: Record<EstadoCategoria, string> = {
  pendiente: "bg-state-pendiente/10 text-state-pendiente border-state-pendiente/20",
  "en-revision": "bg-state-en-revision/10 text-state-en-revision border-state-en-revision/20",
  observado: "bg-state-observado/10 text-state-observado border-state-observado/30",
  aprobado: "bg-state-aprobado/10 text-state-aprobado border-state-aprobado/20",
  insuficiente: "bg-state-insuficiente/10 text-state-insuficiente border-state-insuficiente/20",
  desaprobado: "bg-state-desaprobado/10 text-state-desaprobado border-state-desaprobado/20",
  vencido: "bg-state-vencido/10 text-state-vencido border-state-vencido/20",
  finalizado: "bg-state-finalizado/10 text-state-finalizado border-state-finalizado/20",
};
